import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Alert, CircularProgress, Paper, Chip, Stack, Tabs, Tab, Button } from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useGetCaseManagementQuery, useUpdateCaseManagementMutation } from '../../../services/CaseManagementService';
import { useGetEncounterQuery } from '../../../services/EncounterService';
import { useAppDispatch, useAppSelector } from '../../../app/store';
import { PatientInfoView, CodingReviewTab, ClinicalReviewTab, NotesTab, CallLogTab, DocumentRequestTab } from '../components';

// Types for better type safety
interface CaseManEditParams {
  company: string | null;
  center: string | null;
  id: string | null;
}

// Tab indices for better maintainability
const TAB_INDICES = {
  CODING_REVIEW: 0,
  CLINICAL_REVIEW: 1,
  NOTES: 2,
  CALL_LOG: 3,
  DOCUMENT_REQUEST: 4
};

type TabIndex = typeof TAB_INDICES[keyof typeof TAB_INDICES];

// Styled components for consistent styling
const PAGE_STYLES = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    py: 1.5,
    px: 2,
    minWidth: '800px'
  },
  loadingContainer: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    py: 1.5,
    px: 2,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }
} as const;

const CaseManEdit: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabIndex>(TAB_INDICES.CODING_REVIEW);

  const dispatch = useAppDispatch();
  const { currentCase, isEdited } = useAppSelector(state => state.caseManage);

  // Extract URL parameters
  const params: CaseManEditParams = {
    company: searchParams.get('company'),
    center: searchParams.get('center'),
    id: searchParams.get('id')
  };

  // Event handlers
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSaveChanges = async () => {
    if (!currentCase && !caseData) return;
    if (!params.id) return;

    const baseCase = currentCase || caseData!;

    try {
      const updateRequest = {
        caseManageView: {
          ...baseCase,
          id: params.id,
        }
      };

      await updateCaseManagement(updateRequest).unwrap();
      console.log('Changes saved successfully');
    } catch (error) {
      console.error('Failed to save changes:', error);
    }
  };

  const handleDiscardChanges = () => {
    // TODO: Replace with proper Redux reset action instead of page reload
    window.location.reload();
  };

  // API queries and mutations
  const {
    data: caseData,
    isLoading: caseLoading,
    error: caseError,
    refetch: refetchCaseData
  } = useGetCaseManagementQuery(
    { id: params.id! }, 
    { skip: !params.id }
  );

  const {
    data: encounterData,
    isLoading: encounterLoading,
    error: encounterError
  } = useGetEncounterQuery(
    { id: params.id! }, 
    { skip: !params.id }
  );

  const [updateCaseManagement, { 
    isLoading: isSaving,
    error: saveError 
  }] = useUpdateCaseManagementMutation();

  // Computed values
  const isLoading = caseLoading || encounterLoading;
  const hasError = caseError || encounterError;

  // Early returns for error states
  if (!params.company || !params.center || !params.id) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Missing required parameters: company, center, and id are required.
        </Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={PAGE_STYLES.loadingContainer}>
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body1">Loading case management details...</Typography>
        </Stack>
      </Box>
    );
  }

  if (hasError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load case management or encounter data. Please try again.
        </Alert>
      </Box>
    );
  }

  // Component functions for tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case TAB_INDICES.CODING_REVIEW:
        return <CodingReviewTab />;
      case TAB_INDICES.CLINICAL_REVIEW:
        return <ClinicalReviewTab />;
      case TAB_INDICES.NOTES:
        return <NotesTab />;
      case TAB_INDICES.CALL_LOG:
        return <CallLogTab />;
      case TAB_INDICES.DOCUMENT_REQUEST:
        return <DocumentRequestTab />;
      default:
        return null;
    }
  };

  return (
    <Box sx={PAGE_STYLES.container}>
      {/* Header */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" component="h1" sx={{ mb: 0.5, fontWeight: 600 }}>
              Case Management - Edit Case
              {isEdited && (
                <Chip 
                  label="Unsaved Changes" 
                  size="small" 
                  color="warning" 
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Case ID: {params.id} | Company: {params.company} | Center: {params.center}
            </Typography>
          </Box>
          
          {isEdited && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleDiscardChanges}
                sx={{ textTransform: 'none' }}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleSaveChanges}
                disabled={isSaving}
                sx={{ 
                  textTransform: 'none',
                  backgroundColor: '#162346',
                  '&:hover': { backgroundColor: '#1a2a52' }
                }}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Patient Summary & Insurance Information */}
      <PatientInfoView caseData={caseData} encounterData={encounterData} />

      {/* Save Error Alert */}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to save changes. Please try again.
        </Alert>
      )}

      {/* Tab Strip */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.95rem'
            }
          }}
        >
          <Tab label="Coding Review" />
          <Tab label="Clinical Review" />
          <Tab label="Notes" />
          <Tab label="Call Log" />
          <Tab label="Document Request" />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ p: 3 }}>
          {renderTabContent()}
        </Box>
      </Paper>
    </Box>
  );
};

export default CaseManEdit;
